/**
 * REDCap / CDISC ODM form-metadata import (Phase 1).
 *
 * Loads an ODM 1.3 export (REDCap extensions supported), previews every
 * FormDef with its resolved field types, and writes the selected forms into
 * `trial_protocol_forms` for a chosen trial site.
 *
 * Metadata only — no clinical data (SubjectData) is read or written here.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/layout/Layout";
import { SEOHead } from "@/components/seo/SEOHead";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, ArrowLeft, CheckCircle2, FileCode2, Loader2, ShieldAlert, Upload } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { parseOdmXml, type OdmFormImport, type OdmImportResult } from "@/lib/ecrf/odmImporter";
import { SERURUBELE_SITE_ID } from "@/utils/serurubeleRedcapTransformer";

const sb: any = supabase;

export default function RedcapOdmFormImport() {
  const queryClient = useQueryClient();
  const [siteId, setSiteId] = useState<string>(SERURUBELE_SITE_ID);
  const [parsed, setParsed] = useState<OdmImportResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  const { data: sites } = useQuery({
    queryKey: ["odm-import-sites"],
    queryFn: async () => {
      const { data, error } = await sb
        .from("trial_sites_safe")
        .select("id, name, country")
        .order("name");
      if (error) throw error;
      return (data || []) as { id: string; name: string; country: string | null }[];
    },
  });

  const { data: existingForms } = useQuery({
    queryKey: ["odm-import-existing-forms", siteId],
    enabled: !!siteId,
    queryFn: async () => {
      const { data, error } = await sb
        .from("trial_protocol_forms")
        .select("id, form_name, version, is_active")
        .eq("trial_site_id", siteId);
      if (error) throw error;
      return (data || []) as { id: string; form_name: string; version: string | null }[];
    },
  });

  const existingByName = useMemo(() => {
    const m = new Map<string, { id: string; version: string | null }>();
    (existingForms || []).forEach((f) => m.set(f.form_name, { id: f.id, version: f.version }));
    return m;
  }, [existingForms]);

  const selectedForms = useMemo(
    () => (parsed?.forms || []).filter((f) => selected[f.odm_form_oid]),
    [parsed, selected],
  );

  const piiCount = useMemo(
    () =>
      (parsed?.forms || []).reduce(
        (n, f) => n + f.schema.fields.filter((x) => x.pii).length,
        0,
      ),
    [parsed],
  );

  const handleFile = async (file: File) => {
    setBusy(true);
    setParseError(null);
    setParsed(null);
    setSelected({});
    setFileName(file.name);
    try {
      const text = await file.text();
      const result = parseOdmXml(text);
      setParsed(result);
      const sel: Record<string, boolean> = {};
      result.forms.forEach((f) => (sel[f.odm_form_oid] = true));
      setSelected(sel);
    } catch (err) {
      setParseError(err instanceof Error ? err.message : "Failed to parse ODM file");
    } finally {
      setBusy(false);
    }
  };

  const importMutation = useMutation({
    mutationFn: async (forms: OdmFormImport[]) => {
      if (!siteId) throw new Error("Choose a trial site first");
      const version = (parsed?.sourceDateTime || new Date().toISOString()).slice(0, 10);
      let inserted = 0;
      let updated = 0;

      for (const f of forms) {
        const schema = {
          formTitle: f.schema.formTitle,
          formDescription: f.schema.formDescription,
          fields: f.schema.fields,
          source: parsed?.sourceSystem?.toLowerCase().includes("redcap")
            ? "redcap_odm_v1_3"
            : "cdisc_odm_v1_3",
          odm_form_oid: f.odm_form_oid,
          odm_study_oid: parsed?.studyOid,
          odm_metadata_version: parsed?.metaDataVersion,
          imported_from_file: fileName,
        };
        const existing = existingByName.get(f.formName);
        if (existing) {
          const { error } = await sb
            .from("trial_protocol_forms")
            .update({ form_schema: schema, version, is_active: true })
            .eq("id", existing.id);
          if (error) throw error;
          updated++;
        } else {
          const { error } = await sb.from("trial_protocol_forms").insert({
            trial_site_id: siteId,
            form_name: f.formName,
            form_type: f.formType,
            form_schema: schema,
            version,
            is_active: true,
          });
          if (error) throw error;
          inserted++;
        }
      }
      return { inserted, updated };
    },
    onSuccess: ({ inserted, updated }) => {
      queryClient.invalidateQueries({ queryKey: ["odm-import-existing-forms", siteId] });
      queryClient.invalidateQueries({ queryKey: ["ecrf-forms", siteId] });
      toast.success(`Imported ${inserted} new form(s), updated ${updated}`);
    },
    onError: (err: any) => toast.error("Import failed", { description: err?.message ?? String(err) }),
  });

  const allSelected = !!parsed && parsed.forms.every((f) => selected[f.odm_form_oid]);

  return (
    <>
      <SEOHead
        title="REDCap / ODM Form Import — BRIDGE Admin"
        description="Import CDISC ODM and REDCap form metadata into a trial site's eCRF library."
      />
      <Layout>
        <main className="container mx-auto max-w-5xl px-4 py-8 space-y-6">
          <Button variant="ghost" size="sm" asChild className="-ml-2">
            <Link to="/admin">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Admin Dashboard
            </Link>
          </Button>
          <header className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">REDCap / ODM form import</h1>
              <p className="text-muted-foreground">
                Step 1 of the migration: bring form definitions across. No patient data is read
                from the file on this screen.
              </p>
            </div>
            <Button variant="outline" asChild>
              <Link to="/admin/redcap-odm-data-import">Phase 2: clinical data →</Link>
            </Button>
          </header>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">1. Choose the destination site</CardTitle>
              <CardDescription>Forms are written to that site's eCRF library.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Label htmlFor="site">Trial site</Label>
              <Select value={siteId} onValueChange={setSiteId}>
                <SelectTrigger id="site" className="max-w-xl">
                  <SelectValue placeholder="Select a trial site" />
                </SelectTrigger>
                <SelectContent>
                  {(sites || []).map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                      {s.country ? ` — ${s.country}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {existingForms?.length ?? 0} form(s) already defined for this site.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">2. Upload the ODM export</CardTitle>
              <CardDescription>
                CDISC ODM 1.3 XML. REDCap exports keep their field types, notes, branching logic
                and identifier flags.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <input
                type="file"
                accept=".xml,text/xml,application/xml"
                className="block w-full text-sm file:mr-3 file:rounded-md file:border file:bg-muted file:px-3 file:py-1.5"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                }}
              />
              {busy && (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Parsing {fileName}…
                </p>
              )}
              {parseError && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Could not parse the file</AlertTitle>
                  <AlertDescription className="text-xs">{parseError}</AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {parsed && (
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileCode2 className="h-4 w-4" />
                      3. Review {parsed.forms.length} form(s)
                    </CardTitle>
                    <CardDescription>
                      {parsed.studyName || parsed.studyOid || "Study"} ·{" "}
                      {parsed.sourceDateTime?.slice(0, 10) || "no export date"}
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => {
                    const next: Record<string, boolean> = {};
                    parsed.forms.forEach((f) => (next[f.odm_form_oid] = !allSelected));
                    setSelected(next);
                  }}>
                    {allSelected ? "Clear all" : "Select all"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {piiCount > 0 && (
                  <Alert>
                    <ShieldAlert className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      {piiCount} field(s) are flagged as identifiers in REDCap. They are imported
                      with a <code>pii</code> marker so downstream exports can mask them.
                    </AlertDescription>
                  </Alert>
                )}
                {parsed.warnings.length > 0 && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>{parsed.warnings.length} warning(s)</AlertTitle>
                    <AlertDescription>
                      <ScrollArea className="max-h-24">
                        <ul className="list-disc pl-4 text-xs">
                          {parsed.warnings.map((w, i) => (
                            <li key={i}>{w}</li>
                          ))}
                        </ul>
                      </ScrollArea>
                    </AlertDescription>
                  </Alert>
                )}

                <ScrollArea className="h-[420px] rounded border">
                  <ul className="divide-y">
                    {parsed.forms.map((f) => {
                      const existing = existingByName.get(f.formName);
                      return (
                        <li key={f.odm_form_oid} className="flex items-start gap-3 p-3">
                          <Checkbox
                            id={f.odm_form_oid}
                            checked={!!selected[f.odm_form_oid]}
                            onCheckedChange={(v) =>
                              setSelected((s) => ({ ...s, [f.odm_form_oid]: !!v }))
                            }
                            aria-label={`Import ${f.formName}`}
                          />
                          <div className="min-w-0 flex-1">
                            <Label htmlFor={f.odm_form_oid} className="font-medium">
                              {f.formName}
                            </Label>
                            <div className="mt-1 flex flex-wrap gap-1.5">
                              <Badge variant="outline">{f.formType}</Badge>
                              <Badge variant="secondary">
                                {f.schema.fields.length} fields
                              </Badge>
                              {f.schema.fields.some((x) => x.pii) && (
                                <Badge variant="destructive">contains identifiers</Badge>
                              )}
                              {existing ? (
                                <Badge variant="outline">updates existing</Badge>
                              ) : (
                                <Badge variant="outline">new</Badge>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </ScrollArea>

                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs text-muted-foreground">
                    {selectedForms.length} of {parsed.forms.length} selected
                  </p>
                  <Button
                    onClick={() => importMutation.mutate(selectedForms)}
                    disabled={!selectedForms.length || importMutation.isPending || !siteId}
                  >
                    {importMutation.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="mr-2 h-4 w-4" />
                    )}
                    Import selected forms
                  </Button>
                </div>

                {importMutation.isSuccess && (
                  <Alert>
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      Forms are live in the site's eCRF library. Clinical data (SubjectData) is a
                      separate step and was not imported.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}
        </main>
      </Layout>
    </>
  );
}
